import React, { useEffect, useRef, useState } from 'react';
import Modal from '@/components/modals/Modal';
import ModalHeader from '@/components/modals/ModalHeader';
import ModalFooter from '@/components/modals/ModalFooter';
import FileUpload, { type FileUploadRef, type FileUploadResult } from '@/lib/file-upload';
import { env, validateUploadCredentials } from '@/config/env';
import { employeeApi } from '@/api/employee.api';
import { useAuthStore } from '@/store/auth.store';
import { useQueryClient } from '@tanstack/react-query';

export interface UploadPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPhotoUrl?: string;
  employeeId?: string | number;
  onSuccess?: (photoUrl: string) => void;
}

export const UploadPhotoModal: React.FC<UploadPhotoModalProps> = ({
  isOpen,
  onClose,
  currentPhotoUrl,
  employeeId,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const fileRef = useRef<FileUploadRef>(null);
  const authenticatedEmployee = useAuthStore((s) => s.authenticatedEmployee);
  const user = useAuthStore((s) => s.user);

  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isReplacing = Boolean(currentPhotoUrl);

  // Authenticate upload service when modal opens
  useEffect(() => {
    if (!isOpen) {
      setPendingUrl('');
      setSaveError(null);
      return;
    }

    const validation =
      typeof validateUploadCredentials === 'function'
        ? validateUploadCredentials()
        : { valid: true };
    if (!validation.valid) {
      setCredentialError(validation.error ?? 'Upload credentials not configured');
      return;
    }

    let cancelled = false;

    const authenticate = async () => {
      try {
        let token = sessionStorage.getItem('upload_token');
        if (!token) {
          const baseUrl = env.apiBaseUrl.replace(/\/$/, '');
          const res = await fetch(`${baseUrl}/v2/users/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              username: env.policyUploadEmail,
              password: env.policyUploadPassword,
            }),
          });
          const data = await res.json();
          token = data?.data?.accessToken;
          if (token) {
            sessionStorage.setItem('upload_token', token);
          }
        }

        if (cancelled) return;

        if (token) {
          setUploadToken(token);
          setCredentialError(null);
        } else {
          setCredentialError('Failed to authenticate upload service. Check credentials.');
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to get upload token', e);
          setCredentialError('Failed to connect to upload service');
        }
      }
    };

    void authenticate();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleUploadComplete = (result: FileUploadResult | null) => {
    if (result?.url) {
      // Strip query parameters from the uploaded url
      let cleanUrl = result.url.split('?')[0];
      if (cleanUrl.startsWith('/')) {
        cleanUrl = `${env.talentchekApiBaseUrl}${cleanUrl}`;
      }
      setPendingUrl(cleanUrl);
      setSaveError(null);
    }
  };

  const handleClear = () => {
    setPendingUrl('');
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!pendingUrl) return;

    const targetId = String(employeeId ?? authenticatedEmployee?.id ?? user?.id ?? '');
    if (!targetId) {
      setSaveError('Employee ID not found. Please try again.');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      // Confirm the upload in storage
      if (fileRef.current?.confirm) {
        try {
          await fileRef.current.confirm();
        } catch (e) {
          console.warn('Storage confirm warning:', e);
        }
      }

      // Update employee record with photo URL only
      await employeeApi.update(targetId, { photo: pendingUrl });

      // Update in-memory auth store
      useAuthStore.getState().updateAuthenticatedEmployee({ photo: pendingUrl });

      // Invalidate relevant react-query caches
      queryClient?.invalidateQueries({ queryKey: ['employee', targetId] });
      queryClient?.invalidateQueries({ queryKey: ['employees'] });
      queryClient?.invalidateQueries({ queryKey: ['lookup', 'employees'] });

      onSuccess?.(pendingUrl);
      handleClose();
    } catch (err: any) {
      console.error('Failed to update employee photo:', err);
      setSaveError(err?.message || 'Unable to save profile photo. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setPendingUrl('');
    setSaveError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalHeader
        title={isReplacing ? 'Replace Profile Photo' : 'Upload Profile Photo'}
        onClose={handleClose}
      />

      <div className="space-y-4 px-6 py-5">
        {/* Preview of current photo if replacing and no new photo selected */}
        {currentPhotoUrl && !pendingUrl && (
          <div className="flex flex-col items-center gap-2 py-1">
            <img
              src={currentPhotoUrl}
              alt="Current profile"
              className="h-20 w-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700 shadow-sm"
              data-testid="current-photo-preview"
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">Current photo</span>
          </div>
        )}

        {/* Preview of newly uploaded photo ready to save */}
        {pendingUrl && (
          <div className="flex flex-col items-center gap-2 py-1">
            <img
              src={pendingUrl}
              alt="New profile preview"
              className="h-20 w-20 rounded-full object-cover border-2 border-accent-500 shadow-sm"
              data-testid="new-photo-preview"
            />
            <span className="text-xs font-semibold text-green-600 dark:text-green-400">
              Photo uploaded. Click Save to apply.
            </span>
          </div>
        )}

        {/* Existing FileUpload component */}
        {credentialError ? (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {credentialError}
          </p>
        ) : uploadToken ? (
          <FileUpload
            ref={fileRef}
            baseURL={env.apiBaseUrl.replace(/\/$/, '')}
            token={uploadToken}
            label={isReplacing ? 'Choose a replacement photo' : 'Choose a profile photo'}
            maxFiles={1}
            onUploadComplete={handleUploadComplete}
            onClear={handleClear}
          />
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Initializing upload service…</p>
        )}

        {saveError && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {saveError}
          </p>
        )}
      </div>

      <ModalFooter>
        <button
          type="button"
          className="btn-secondary"
          disabled={isSaving}
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!pendingUrl || isSaving}
          onClick={handleSave}
          data-testid="save-photo-btn"
        >
          {isSaving ? 'Saving…' : 'Save Photo'}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default UploadPhotoModal;
